import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout'
import { LEGAL_ENTITY as ED } from './legalEntity'

/**
 * GDPR notice (articles 13 and 14).
 *
 * The two-hat distinction below is the heart of this document and must not be smoothed over:
 * ISEFFI is the controller for subscriber accounts and billing, but a processor for the business
 * data displayed in each portal, which belongs to the subscribing company.
 */
export default function Confidentialite() {
  return (
    <LegalLayout
      title="Politique de confidentialité"
      description="Traitement des données personnelles par ISEFFI dans le cadre du service Espace Client : finalités, bases légales, durées, sous-traitants et droits des personnes."
    >
      <h2>1. Qui traite vos données, et à quel titre</h2>
      <p>
        Le service {ED.service} est fourni par {ED.societe}, {ED.adresse}. Deux situations doivent
        être distinguées, car elles n’emportent pas les mêmes responsabilités :
      </p>
      <ul>
        <li>
          <strong>{ED.societe} est responsable de traitement</strong> pour les données liées à la
          relation avec ses propres clients et aux comptes du portail : identité et coordonnées des
          utilisateurs, données de connexion, données de facturation et d’abonnement, échanges avec
          le support.
        </li>
        <li>
          <strong>{ED.societe} est sous-traitant</strong>, au sens de l’article 28 du RGPD, pour les
          données métier affichées dans chaque espace (factures, commandes, devis, expéditions,
          documents, tickets). Ces données proviennent du système de gestion de l’entreprise
          utilisatrice, qui en reste responsable de traitement et en définit seule les finalités. Si
          vous êtes client d’une de ces entreprises, adressez-lui directement vos demandes ; nous
          les relaierons si vous nous les adressez.
        </li>
      </ul>
      <p>Contact pour toute question relative aux données : {ED.email}.</p>
      <p>Délégué à la protection des données : {ED.dpo}.</p>

      <h2>2. Données collectées et finalités</h2>
      <ul>
        <li>
          <strong>Compte utilisateur</strong> (nom, prénom, adresse e-mail, entreprise de
          rattachement, rôle, langue et préférences d’affichage) : créer et administrer l’accès au
          portail. Base légale : exécution du contrat.
        </li>
        <li>
          <strong>Données de connexion et de sécurité</strong> (jeton de session, horodatage et
          adresse IP des connexions, journal des actions sensibles) : sécuriser le service, détecter
          les abus et tracer les opérations. Base légale : intérêt légitime à la sécurité du
          service, et obligation de sécurité de l’article 32 du RGPD.
        </li>
        <li>
          <strong>Données d’abonnement et de facturation</strong> (formule souscrite, consommation
          des quotas, factures, coordonnées de facturation) : gérer l’abonnement, encaisser les
          paiements et respecter les obligations comptables. Bases légales : exécution du contrat et
          obligation légale.
        </li>
        <li>
          <strong>Demandes de contact, d’inscription et de support</strong> : traiter la demande.
          Base légale : consentement pour les demandes spontanées, exécution du contrat ou mesures
          précontractuelles pour les autres.
        </li>
        <li>
          <strong>Données métier issues du système de gestion</strong> : les afficher dans l’espace
          de l’utilisateur, envoyer les notifications associées et, le cas échéant, permettre la
          signature électronique des devis. Ce traitement est réalisé pour le compte de l’entreprise
          utilisatrice, sur ses instructions.
        </li>
      </ul>

      <h2>3. Destinataires et sous-traitants</h2>
      <p>
        Les données ne sont ni vendues, ni louées, ni cédées à des tiers à des fins de prospection.
        Elles sont accessibles aux personnes habilitées de {ED.societe} et aux administrateurs
        désignés par l’entreprise utilisatrice, dans la limite de leur périmètre. Les prestataires
        suivants interviennent, chacun n’accédant qu’aux données nécessaires à sa prestation :
      </p>
      <ul>
        <li>
          <strong>Hébergement</strong> : {ED.hebergeur}, serveurs situés en{' '}
          {ED.hebergeurLocalisation}.
        </li>
        <li>
          <strong>Envoi des e-mails</strong> (notifications, relances, réinitialisation de mot de
          passe) : {ED.sousTraitantEmail}.
        </li>
        <li>
          <strong>Paiement des abonnements</strong> : {ED.sousTraitantPaiement}. Les données de
          carte bancaire sont saisies directement chez le prestataire et ne transitent jamais par
          nos serveurs.
        </li>
        <li>
          <strong>Signature électronique</strong>, lorsqu’elle est activée :{' '}
          {ED.sousTraitantSignature}. Le document et l’identité du signataire lui sont alors
          transmis.
        </li>
        <li>
          <strong>Facturation électronique</strong>, lorsqu’elle est activée :{' '}
          {ED.sousTraitantFacturation}, pour la transmission des factures dans le cadre du
          dispositif national.
        </li>
        <li>
          <strong>Fonctionnalités d’intelligence artificielle</strong>, lorsqu’elles sont activées
          (assistance à la saisie, enrichissement de catalogue, assistant vocal) :{' '}
          {ED.sousTraitantIA}. Seul le contenu nécessaire à la demande leur est transmis.
        </li>
      </ul>
      <p>
        Certains de ces prestataires peuvent traiter des données hors de l’Union européenne. Ces
        transferts sont alors encadrés par les clauses contractuelles types de la Commission
        européenne ou par une décision d’adéquation. La liste à jour des sous-traitants et les
        garanties applicables peuvent être obtenues sur demande à {ED.email}.
      </p>

      <h2>4. Durées de conservation</h2>
      <ul>
        <li>
          <strong>Compte utilisateur</strong> : pendant toute la durée de la relation contractuelle,
          puis suppression ou anonymisation dans un délai raisonnable après sa fin.
        </li>
        <li>
          <strong>Journaux de connexion et d’activité</strong> : douze mois, durée usuellement
          retenue pour les besoins de sécurité et de preuve.
        </li>
        <li>
          <strong>Factures et pièces comptables</strong> : dix ans, conformément à l’article L123-22
          du code de commerce.
        </li>
        <li>
          <strong>Demandes de contact non suivies d’un contrat</strong> : trois ans à compter du
          dernier échange.
        </li>
        <li>
          <strong>Données métier</strong> : selon les instructions de l’entreprise utilisatrice, et
          au plus tard jusqu’au terme de son abonnement, sous réserve des durées d’archivage qui
          s’imposent à elle.
        </li>
      </ul>

      <h2>5. Cookies et stockage local</h2>
      <p>
        Le site n’utilise ni cookie publicitaire, ni traceur tiers à des fins de mesure d’audience
        ou de suivi comportemental. Le stockage local du navigateur (localStorage) est utilisé
        uniquement pour le fonctionnement de l’espace authentifié — jeton de session — et pour
        mémoriser vos préférences d’affichage : thème clair ou sombre, langue, état de la barre
        latérale. Ces éléments sont strictement nécessaires au service ou relèvent de vos
        préférences, et ne requièrent donc pas de consentement préalable au sens de l’article 82 de
        la loi Informatique et Libertés.
      </p>

      <h2>6. Sécurité</h2>
      <p>
        Les échanges avec le service sont chiffrés en transit. Les mots de passe sont stockés sous
        forme de condensat non réversible, l’accès aux données est cloisonné par entreprise et par
        rôle, et les actions sensibles sont journalisées. En cas de violation de données susceptible
        d’engendrer un risque élevé pour vos droits, vous en serez informé conformément à l’article
        34 du RGPD.
      </p>

      <h2>7. Vos droits</h2>
      <p>
        Vous disposez des droits d’accès, de rectification, d’effacement, de limitation,
        d’opposition et de portabilité, ainsi que du droit de définir des directives relatives au
        sort de vos données après votre décès. Ces droits s’exercent auprès de {ED.email}, en
        justifiant de votre identité en cas de doute raisonnable. Une réponse vous sera adressée
        dans un délai d’un mois, prorogeable de deux mois si la demande est complexe.
      </p>
      <p>
        Lorsque la demande porte sur des données métier affichées pour le compte d’une entreprise
        utilisatrice, elle est transmise à celle-ci, seule habilitée à y répondre.
      </p>
      <p>
        Vous pouvez introduire une réclamation auprès de la Commission nationale de l’informatique
        et des libertés :{' '}
        <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
          www.cnil.fr
        </a>{' '}
        — 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07.
      </p>

      <h2>8. Modification de la présente politique</h2>
      <p>
        La présente politique peut être mise à jour pour tenir compte des évolutions du service ou
        de la réglementation. La date de dernière mise à jour figure en tête de page. Les
        modifications substantielles sont portées à la connaissance des utilisateurs par e-mail ou
        par une information dans le portail. Voir également les{' '}
        <Link to="/cgu">conditions générales d’utilisation</Link>.
      </p>
    </LegalLayout>
  )
}
